import React, { useContext, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { useNavigate } from "react-router";
import {
  Subscription,
  AggregatedFundCommissionStatistics,
} from "@rentablo/api/apiv1";
import { MandatorContext } from "@rentablo/theming";
import DollarIcon from "@icons/myicons/dollar.svg?react";
import { toCurrency } from "@helpers";
import { Banner } from "@rentablo/components";
import { appNameMap } from "@translations/i18n";

type Props = {
  subscriptions?: Subscription[];
  fundCommissions?: AggregatedFundCommissionStatistics;
};

export const CashbackBanner = ({ subscriptions, fundCommissions }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isNotificationHidden, setIsNotificationHidden] = useState(
    localStorage.getItem("cashbackNotifHidden") === "true"
  );
  const { isOnvista } = useContext(MandatorContext);

  const shouldShowCashbackNotif =
    !isNotificationHidden &&
    subscriptions &&
    !subscriptions?.some((subscription) =>
      [
        "in_progress",
        "internal_processing",
        "processing_at_bank",
        "processed",
      ].includes(subscription.status || "")
    ) &&
    ["CHECK", "ACTION_REQUIRED"].includes(
      fundCommissions?.cashbackAdvantage?.commissionStatus!
    );

  const name = isOnvista
    ? "Onvista"
    : appNameMap[import.meta.env.VITE_APP_NAME || "rentablo"];

  const hideCashbackNotif = () => {
    setIsNotificationHidden(true);
    localStorage.setItem("cashbackNotifHidden", "true");
  };

  if (!shouldShowCashbackNotif) return null;

  return (
    <Banner
      type="info"
      icon={<DollarIcon />}
      title={t("dashboardPage.dashboard.cashbackBanner.title")}
      text={
        <Trans
          i18nKey="dashboardPage.dashboard.cashbackBanner.text"
          values={{
            amount: toCurrency(
              fundCommissions?.cashbackAdvantage?.cashbackAdvantage
            ),
            name: name,
          }}
          components={{
            strongText: (
              <Typography
                variant="body3"
                color="textPrimary"
                component="span"
              />
            ),
          }}
        />
      }
      breakpoint="md"
      mb={6}
      primaryButton={{
        label: t("dashboardPage.dashboard.cashbackBanner.button"),
        onClick: () => navigate("/cashback-wizard-start"),
      }}
      secondaryButton={{
        label: t("dashboardPage.dashboard.cashbackBanner.cancel"),
        onClick: hideCashbackNotif,
      }}
    />
  );
};
